// Native Shopify forms share one purchase type and bottle count across both offers.
// Each bottle count carries its own price and refill plan; a subscribed order repeats at the same price.
if (!customElements.get('steadia-buybox')) {
  customElements.define('steadia-buybox', class extends HTMLElement {
    connectedCallback() {
      this.unit = Number(this.dataset.unit);
      this.fmt = this.dataset.moneyFormat;
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      this.modes = [...this.querySelectorAll('[data-purchase-type]')];
      // Stacked rows use one subscribe checkbox in place of the two purchase-type tabs.
      this.toggle = this.querySelector('[data-sub-toggle]');
      if (!this.tiers.length) return;
      this.tier = this.tiers.find((button) => button.getAttribute('aria-checked') === 'true') || this.tiers[0];
      this.sub = this.toggle ? this.toggle.checked
        : this.modes.some((button) => button.dataset.purchaseType === 'subscribe' && button.getAttribute('aria-checked') === 'true');
      this.toggle?.addEventListener('change', () => {
        this.sub = this.toggle.checked;
        this.render();
        this.share();
      });
      this.tiers.forEach((button) => button.addEventListener('click', () => {
        this.tier = button;
        this.render();
        this.share();
      }));
      this.modes.forEach((button) => button.addEventListener('click', () => {
        this.sub = button.dataset.purchaseType === 'subscribe';
        this.render();
        this.share();
      }));
      [this.tiers, this.modes].forEach((buttons) => buttons.forEach((button) => {
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (buttons.indexOf(button) + step + buttons.length) % buttons.length;
          buttons[index].click();
          buttons[index].focus();
        });
      }));
      this.onShared = (event) => {
        if (event.detail.from === this) return;
        this.tier = this.tiers.find((button) => button.dataset.tier === event.detail.tier) || this.tier;
        this.sub = (this.modes.length > 0 || Boolean(this.toggle)) && event.detail.sub;
        this.render();
      };
      document.addEventListener('steadia:offer', this.onShared);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('steadia:offer', this.onShared);
    }
    share() {
      document.dispatchEvent(new CustomEvent('steadia:offer', {
        detail: { tier: this.tier.dataset.tier, sub: this.sub, from: this },
      }));
    }
    money(cents) {
      const [whole, decimal] = (cents / 100).toFixed(2).split('.');
      const comma = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => ({
        amount: `${comma}.${decimal}`, amount_no_decimals: comma,
        amount_with_comma_separator: `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`,
        amount_no_decimals_with_comma_separator: whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      }[key] ?? `${comma}.${decimal}`));
    }
    price(tier) {
      const quantity = Number(tier.dataset.qty);
      const each = Number(this.sub ? tier.dataset.subEach : tier.dataset.onceEach);
      const total = each * quantity;
      const compare = this.unit * quantity;
      return { total, compare, each, save: Math.round((compare - total) / 100) * 100 };
    }
    dollars(cents) {
      return this.money(cents).replace(/[.,]00$/, '');
    }
    render() {
      this.modes.forEach((button) => {
        const selected = (button.dataset.purchaseType === 'subscribe') === this.sub;
        button.setAttribute('aria-checked', selected);
        button.tabIndex = selected ? 0 : -1;
      });
      if (this.toggle) this.toggle.checked = this.sub;
      this.tiers.forEach((tier) => {
        tier.setAttribute('aria-checked', tier === this.tier);
        tier.tabIndex = tier === this.tier ? 0 : -1;
        const price = this.price(tier);
        tier.querySelector('[data-price]').textContent = this.money(price.total);
        const compare = tier.querySelector('[data-compare]');
        compare.textContent = this.money(price.compare);
        compare.hidden = price.compare <= price.total;
        const each = tier.querySelector('[data-each]');
        if (each) each.textContent = each.dataset.template.replace('[price]', this.money(price.each));
        const save = tier.querySelector('[data-save]');
        if (save) {
          save.textContent = save.dataset.template.replace('[amount]', this.dollars(price.save));
          save.hidden = price.save < 100;
        }
      });
      const termLine = this.querySelector('[data-term-line]');
      if (termLine) termLine.textContent = `${this.tier.dataset.name} · ${this.sub ? this.tier.dataset.schedule : this.tier.dataset.supply}`;
      const price = this.price(this.tier);
      this.querySelector('[data-btn-price]').textContent = this.money(price.total);
      this.querySelector('[data-qty-input]').value = this.tier.dataset.qty;
      const plan = this.querySelector('[data-plan-input]');
      if (plan) {
        plan.value = this.sub ? (this.tier.dataset.plan || plan.dataset.plan) : '';
        plan.disabled = !this.sub;
      }
      const sumPrice = this.querySelector('[data-sum-price]');
      if (sumPrice) {
        sumPrice.textContent = this.money(price.total);
        const sumCompare = this.querySelector('[data-sum-compare]');
        sumCompare.textContent = this.money(price.compare);
        sumCompare.hidden = price.compare <= price.total;
        const sumSave = this.querySelector('[data-sum-save]');
        sumSave.textContent = sumSave.dataset.template.replace('[amount]', this.dollars(price.save));
        sumSave.hidden = price.save < 100;
      }
      this.querySelectorAll('[data-gift]').forEach((gift) => {
        const locked = Number(gift.dataset.min) > Number(this.tier.dataset.qty);
        gift.classList.toggle('is-locked', locked);
        const note = gift.querySelector('[data-gift-note]');
        note.textContent = locked ? note.dataset.locked : note.dataset.open;
      });
      const refill = this.querySelector('[data-refill]');
      if (refill) {
        refill.textContent = refill.dataset.template.replace('[price]', this.money(price.total)).replace('[every]', this.tier.dataset.every);
        refill.hidden = !this.sub;
      }
      const sticky = this.closest('section')?.querySelector('[data-sticky-line]');
      if (sticky) sticky.textContent = `${this.tier.dataset.qty} bottle${Number(this.tier.dataset.qty) > 1 ? 's' : ''} · ${this.money(price.total)}`;
      const stickyPrice = this.closest('section')?.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(price.total);
    }
  });
}
